import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

const tools = [
  {
    number: '01',
    title: 'Deposit Limits',
    body: 'Set daily, weekly, or monthly caps on how much you can deposit. Limits take effect immediately. Increasing a limit requires a 24-hour cooling period — giving you time to reconsider.',
  },
  {
    number: '02',
    title: 'Self-Exclusion',
    body: 'Voluntarily exclude yourself from the platform for 24 hours, 7 days, 30 days, or permanently. During exclusion, trading, depositing, and withdrawals are all blocked. This cannot be reversed during the exclusion period.',
  },
  {
    number: '03',
    title: 'Account Suspension',
    body: 'If you believe your account has been compromised or you need an immediate pause, contact support@minomarkets.app and we will suspend your account within 24 hours.',
  },
];

const guidelinesDo = [
  'Only stake what you can afford to lose',
  'Set limits before you start trading',
  'Take regular breaks from the platform',
  'Treat trading as entertainment, not income',
];
const guidelinesAvoid = [
  'Chase losses by placing larger trades',
  'Trade with money meant for essential expenses',
  'Hide your trading from family or friends',
  'Borrow money to fund your trading',
];

const signs = [
  'Spending more than you can afford to lose',
  'Trading to recover money you have already lost',
  'Neglecting work, family, or essential expenses due to trading',
  'Feeling anxious, irritable, or restless when not trading',
  'Hiding your trading activity from friends or family',
  'Borrowing money to fund your trades',
  'Repeatedly trying to cut back but failing',
  'Thinking about trading constantly throughout the day',
];

const resources = [
  {
    name: 'Gamblers Anonymous Kenya',
    description: 'Peer support meetings and resources for people with gambling problems.',
    contact: 'Available through local AA Kenya chapters',
  },
  {
    name: 'Befrienders Kenya',
    description: 'Emotional support helpline for people in distress, including gambling-related stress.',
    contact: '+254 722 178 177',
  },
  {
    name: 'Kenya Red Cross',
    description: 'Mental health and psychosocial support services across Kenya.',
    contact: '1199 (toll-free)',
  },
  {
    name: 'Mino Support',
    description: 'Our team can help you set limits, self-exclude, or permanently close your account.',
    contact: 'support@minomarkets.app',
  },
];

const faqs = [
  {
    q: 'Can I reverse a self-exclusion?',
    a: 'No. Self-exclusion cannot be reversed during the exclusion period. This is intentional — it protects you from making impulsive decisions to return to the platform.',
  },
  {
    q: 'What happens if I try to create a new account while self-excluded?',
    a: 'Creating a new account to circumvent self-exclusion is a violation of our Terms and Conditions. Any such account will be permanently banned and funds forfeited.',
  },
  {
    q: 'Can I lower my deposit limits immediately?',
    a: 'Yes. You can lower your deposit limits at any time with immediate effect. However, increasing your limits requires a 24-hour cooling period.',
  },
  {
    q: 'Will Mino contact me if I show signs of problem trading?',
    a: 'Yes. If our systems detect unusual trading patterns — such as sudden large increases in stakes, frequent late-night activity, or rapid deposits after withdrawals — our team may reach out to check in.',
  },
  {
    q: 'How do I permanently close my account?',
    a: 'You can delete your account from your Profile page under Danger Zone. This permanently deletes all your data in compliance with the Kenya Data Protection Act 2019. Make sure to withdraw your balance first.',
  },
];

export default function ResponsibleGambling() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [heroRef, heroVisible] = useInView(0.05);

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>Responsible Trading — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="Responsible trading tools and guidelines for Mino Markets users." />
      </Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.12); }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        .mino-aurora {
          position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
        }
        .mino-aurora::before {
          content: '';
          position: absolute;
          width: 140%; height: 55%;
          top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%,
            transparent 0deg,
            rgba(55,114,255,0.09) 40deg,
            rgba(22,199,132,0.07) 80deg,
            rgba(124,58,237,0.08) 120deg,
            transparent 160deg
          );
          filter: blur(60px);
          animation: auroraShift 20s ease-in-out infinite alternate;
          transform-origin: center top;
        }
        .mino-aurora::after {
          content: '';
          position: absolute;
          width: 80%; height: 40%;
          bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%,
            transparent 0deg,
            rgba(22,199,132,0.06) 50deg,
            rgba(55,114,255,0.05) 100deg,
            transparent 150deg
          );
          filter: blur(70px);
          animation: auroraShift2 26s ease-in-out infinite alternate;
          transform-origin: center bottom;
        }
        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }
        .mino-beam {
          position: fixed; pointer-events: none; z-index: 0;
          width: 1px; top: 0; bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
          animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
        }
        @keyframes auroraShift {
          0%   { transform: rotate(-4deg) scaleX(1);    opacity: 1; }
          50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
          100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
        }
        @keyframes auroraShift2 {
          0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
          50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
          100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
        }
        @keyframes beamDrift {
          0%   { left: var(--from); opacity: 0.5; }
          100% { left: var(--to);   opacity: 1; }
        }

        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; }
        .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

        .tool-card { transition: border-color 0.3s ease, background 0.3s ease; }
        .tool-card:hover { border-color: rgba(255,255,255,0.16) !important; background: rgba(255,255,255,0.04) !important; }

        .sign-row { transition: border-color 0.2s ease, background 0.2s ease; }
        .sign-row:hover { border-color: rgba(255,255,255,0.12) !important; background: rgba(255,255,255,0.03) !important; }

        .resource-card { transition: border-color 0.3s ease, background 0.3s ease; }
        .resource-card:hover { border-color: rgba(255,255,255,0.16) !important; background: rgba(255,255,255,0.04) !important; }

        .faq-item { transition: background 0.25s ease, border-color 0.25s ease; }
        .faq-item:hover { background: rgba(255,255,255,0.03) !important; }
      `}</style>

      <div className="mino-aurora" />
      <div className="mino-dot-grid" />
      <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
      <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
      <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {[['Terms', '/terms'], ['Privacy', '/privacy']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
        <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 20px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 400, cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#e8e6e0'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
          Back
        </button>
      </nav>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>

        {/* Hero */}
        <div ref={heroRef} style={{ paddingTop: 120, paddingBottom: 100, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)' }}>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Safe Trading · 18+ Only</p>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(48px,7vw,88px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-3px', color: '#e8e6e0', marginBottom: 36, maxWidth: 720 }}>
            Trade Smart.<br />Stay in Control.
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.36)', lineHeight: 1.8, maxWidth: 480, fontWeight: 300 }}>
            Mino is built for informed trading. Real tools to help you stay in control. If trading stops being enjoyable, we are here to help.
          </p>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Commitment */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 24 }}>Our Commitment</p>
                <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(28px,3.5vw,40px)', fontWeight: 600, lineHeight: 1.12, letterSpacing: '-1.2px', color: '#e8e6e0', marginBottom: 24 }}>
                  Built in, not buried.
                </h2>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>
                  Safe Trading tools are built into the product — not hidden in a help page. Every user has access to deposit limits and self-exclusion directly from their profile at any time.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[['Deposit Limits', 'Daily, weekly, or monthly caps'], ['Self-Exclusion', '24h, 7d, 30d, or permanent'], ['Cooling Period', '24h delay on limit increases'], ['Account Suspension', 'Via support within 24 hours']].map(([label, value], i, a) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', borderBottom: i < a.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.2)', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, color: '#e8e6e0', fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Built-in Tools */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Built-in Protection Tools</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {tools.map((tool, i) => (
                <div key={tool.number} className="tool-card" style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 32, padding: '32px 0', borderBottom: i < tools.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', alignItems: 'start', borderRadius: 4 }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.08em', paddingTop: 3 }}>{tool.number}</span>
                  <div>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.2px' }}>{tool.title}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.32)', lineHeight: 1.8, fontWeight: 300 }}>{tool.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 24, padding: '16px 20px', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', fontWeight: 300 }}>
                Access all tools from{' '}
                <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500, cursor: 'pointer' }} onClick={() => navigate('/profile')}>
                  Profile — Safe Trading tab
                </span>
              </span>
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Guidelines */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Healthy Trading Guidelines</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {/* Do column */}
              <div style={{ padding: '32px 36px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', borderRadius: '16px 0 0 16px' }}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 28 }}>Do</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {guidelinesDo.map((text, i) => (
                    <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, fontWeight: 300 }}>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Avoid column */}
              <div style={{ padding: '32px 36px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', borderRadius: '0 16px 16px 0', borderLeft: 'none' }}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 28 }}>Avoid</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {guidelinesAvoid.map((text, i) => (
                    <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2.5" strokeLinecap="round" style={{ marginTop: 2, flexShrink: 0 }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6, fontWeight: 300 }}>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Warning Signs */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 24 }}>Warning Signs of Problem Trading</p>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.28)', marginBottom: 40, lineHeight: 1.7, fontWeight: 300 }}>
              If you or someone you know is experiencing any of the following, please seek help.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {signs.map((sign, i) => (
                <div key={i} className="sign-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 20, padding: '16px 20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.18)', flexShrink: 0, marginTop: 3, fontWeight: 500, letterSpacing: '0.06em' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.38)', fontSize: 13, lineHeight: 1.65, fontWeight: 300 }}>{sign}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Resources */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Support Resources in Kenya</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {resources.map((r, i) => (
                <div key={r.name} className="resource-card" style={{ padding: '36px 32px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.025)', borderRadius: i === 0 ? '16px 0 0 0' : i === 1 ? '0 16px 0 0' : i === 2 ? '0 0 0 16px' : '0 0 16px 0' }}>
                  <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, color: '#e8e6e0', fontSize: 14, marginBottom: 10, letterSpacing: '-0.2px' }}>{r.name}</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.3)', fontSize: 13, lineHeight: 1.65, marginBottom: 16, fontWeight: 300 }}>{r.description}</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 500 }}>{r.contact}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* FAQ */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Common Questions</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {faqs.map((faq, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className="faq-item" style={{ border: `1px solid ${isOpen ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 12, overflow: 'hidden', background: isOpen ? 'rgba(255,255,255,0.03)' : 'transparent', transition: 'all 0.25s ease' }}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)} style={{ width: '100%', background: 'transparent', border: 'none', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 16, textAlign: 'left', fontFamily: 'inherit' }}>
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 500, color: isOpen ? '#e8e6e0' : 'rgba(255,255,255,0.5)', transition: 'color 0.2s' }}>{faq.q}</span>
                      <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.2)', transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)', transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', flexShrink: 0, lineHeight: 1, fontWeight: 300 }}>+</span>
                    </button>
                    <div style={{ maxHeight: isOpen ? 400 : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.16,1,0.3,1)' }}>
                      <div style={{ padding: '0 28px 24px' }}>
                        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 18 }} />
                        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* CTA */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div onClick={() => navigate('/profile')} style={{ background: '#e8e6e0', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#000', marginBottom: 10, letterSpacing: '-0.5px' }}>Set my limits</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.45)', lineHeight: 1.6, marginBottom: 24, fontWeight: 400 }}>Access deposit limits and self-exclusion from your profile.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Go to profile
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </div>
              <a href="mailto:support@minomarkets.app" style={{ border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', background: 'rgba(255,255,255,0.025)', transition: 'all 0.2s', textDecoration: 'none', display: 'block' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.5px' }}>Contact Support</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6, marginBottom: 24, fontWeight: 300 }}>If you're struggling, don't wait. Reach out directly.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  support@minomarkets.app
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </a>
            </div>
          </FadeUp>
        </div>

      </div>

      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>Trading involves risk. Only trade what you can afford to lose. 18+ only.</span>
        <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
          Back to markets
        </span>
      </div>
    </div>
  );
}